import Icon from '@/components/ui/AppIcon';

interface ProductCardProps {
  title: string;
  subtitle: string;
  benefits: string[];
  imageAlt: string;
  delay?: string;
}

function ProductCard({ title, subtitle, benefits, imageAlt, delay }: ProductCardProps) {
  const imageUrl = title.includes('Layer') 
    ? 'https://images.unsplash.com/photo-1548550023-2bdb3c5beed7?w=800&q=80'
    : 'https://confluence.ag/wp-content/uploads/2025/08/Better-Feed-New.jpg?w=800&q=80';

  return (
    <div className="glass-card rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300 reveal" style={{ animationDelay: delay }}>
      {/* Image */}
      <div className="aspect-[4/3] bg-gradient-to-br from-green-100 to-green-50">
        <img 
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <div className="p-8">
        <h3 className="text-heading-3 text-foreground mb-2">{title}</h3>
        <p className="text-lg text-primary font-semibold mb-6">{subtitle}</p>

        <div className="space-y-4 mb-8">
          <p className="text-sm font-semibold text-foreground">Supports:</p>
          <ul className="space-y-3">
            {benefits.map((benefit, index) => (
              <li key={`benefit_${index}`} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="CheckIcon" size={14} className="text-primary" />
                </div>
                <span className="text-base text-muted-foreground">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <a
          href="/products"
          className="inline-flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground px-6 py-3.5 rounded-full text-base font-bold hover:bg-primary/90 transition-all"
        >
          <span>Learn More</span>
          <Icon name="ArrowRightIcon" size={18} />
        </a>
      </div>
    </div>
  );
}

export default function ProductsSection() {
  const products = [
    {
      id: 'product_layer',
      title: 'Bloom Concentrate for Layer Hens',
      subtitle: 'Maximize Egg Production',
      benefits: [
        'Higher egg yield',
        'Stronger shells',
        'Consistent laying cycles',
        'Improved bird immunity',
      ],
      imageAlt: 'Brown layer hens in clean farm environment with fresh eggs in nesting boxes',
    },
    {
      id: 'product_broiler',
      title: 'Bloom Concentrate for Broiler Hens',
      subtitle: 'Accelerate Growth Performance',
      benefits: [
        'Rapid weight gain',
        'Improved muscle development',
        'Better feed conversion ratio',
        'Reduced mortality',
      ],
      imageAlt: 'Healthy white broiler chickens in modern poultry house with optimal growth conditions',
    },
  ];

  return (
    <section id="products" className="py-section px-6 bg-gradient-to-b from-white to-green-50/30">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <p className="text-primary font-bold uppercase tracking-wider text-sm mb-4">Our Products</p>
          <h2 className="text-heading-2 md:text-[3rem] lg:text-heading-1 leading-tight text-foreground mb-6">
            Premium Dutch-Formulated <br />
            <span className="gradient-text">Concentrates</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            We provide high-performance poultry concentrates designed to help farmers achieve better productivity and profitability.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {products.map((product, index) => (
            <ProductCard
              key={product.id}
              title={product.title}
              subtitle={product.subtitle}
              benefits={product.benefits}
              imageAlt={product.imageAlt}
              delay={`${index * 0.1}s`}
            />
          ))}
        </div>

        {/* View All CTA */}
        <div className="text-center reveal">
          <a
            href="/products"
            className="inline-flex items-center justify-center gap-3 glass-card px-8 py-4 rounded-full text-base font-bold hover:bg-white transition-all"
          >
            <span>View All Products</span>
            <Icon name="ArrowRightIcon" size={20} className="text-primary" />
          </a>
        </div>
      </div>
    </section>
  );
}